import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, Loader2, X } from "lucide-react";
import { http } from "@/lib/api";
import { useLang } from "@/i18n";
import { CareerCard } from "@/components/CareerCard";
import { useIdList, SHORTLIST_KEY } from "@/lib/idList";

const QUICK = [
  ["remote", "Remote-friendly", (c) => c.remote !== "On-site"],
  ["outdoors", "Outdoors", (c) => c.setting !== "Mostly indoors"],
  ["nodegree", "No degree needed", (c) => c.no_degree],
  ["lowphys", "Low physical demand", (c) => c.physical === "Low"],
  ["people", "Works with people", (c) => ["High", "Moderate"].includes(c.public)],
  ["new", "New careers", (c) => c.is_new],
];
const fold = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
const chip = (on) => `rounded-full px-3.5 py-1.5 text-sm border transition-colors ${on ? "bg-[#1C242B] text-white border-[#1C242B]" : "bg-white border-[#1C242B]/15 hover:bg-[#1C242B]/5"}`;

export default function ExploreCareers() {
  const { t } = useLang();
  const { ids: starred } = useIdList(SHORTLIST_KEY);
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const q = params.get("q") || "";
  const cluster = params.get("cluster") || "";
  const quick = (params.get("f") || "").split(",").filter(Boolean);
  const onlyStarred = params.get("starred") === "1";
  const set = (k, v) => { const p = new URLSearchParams(params); v ? p.set(k, v) : p.delete(k); setParams(p, { replace: true }); };

  useEffect(() => { http.get("/careers").then((r) => setData(r.data)); }, []);

  const colors = useMemo(() => Object.fromEntries((data?.clusters || []).map((c) => [c.id, c.color])), [data]);
  const indexed = useMemo(() => (data?.careers || []).map((c) => ({
    ...c, hay: fold([c.title, c.summary, c.cluster_label, c.training, c.en, ...c.labels, ...QUICK.filter(([, , f]) => f(c)).flatMap(([, l]) => [l, t(l)])].join(" ")),
  })), [data, t]);
  const shown = useMemo(() => {
    const words = fold(q).split(/\s+/).filter(Boolean);
    return indexed.filter((c) => (!cluster || c.cluster === cluster) && (!onlyStarred || starred.includes(c.id))
      && quick.every((k) => QUICK.find(([id]) => id === k)?.[2](c))
      && words.every((w) => c.hay.includes(w) || (w.length >= 5 && c.hay.includes(w.slice(0, -1)))));
  }, [indexed, q, cluster, quick, onlyStarred, starred]);

  const toggleQuick = (k) => set("f", (quick.includes(k) ? quick.filter((x) => x !== k) : [...quick, k]).join(","));

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-10 pb-10" data-testid="explore-careers-page">
      <p className="overline-label">Explore careers</p>
      <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mt-3 max-w-3xl">Browse every career, no test needed</h1>
      <p className="mt-4 text-base text-slate-600 max-w-2xl">Search by name or keyword, filter by field, and open any career to see the real picture: environment, gear, training, pay and first steps.</p>

      <div className="mt-8 relative max-w-2xl">
        <Search size={18} className="absolute start-4 top-1/2 -translate-y-1/2 text-slate-400" />
        <input value={q} onChange={(e) => set("q", e.target.value)} placeholder="Try nurse, outdoors, remote, no degree…" data-testid="explore-search-input"
          className="w-full rounded-full border border-[#1C242B]/15 bg-white ps-11 pe-11 py-3.5 text-base focus:outline-none focus:ring-2 focus:ring-[#D96B43]/40" />
        {q && <button onClick={() => set("q", "")} aria-label="Clear search" data-testid="explore-search-clear" className="absolute end-3 top-1/2 -translate-y-1/2 p-1.5 rounded-full hover:bg-[#1C242B]/5"><X size={16} /></button>}
      </div>

      <div className="mt-5 flex flex-wrap gap-2" data-testid="explore-quick-filters">
        <button onClick={() => set("starred", onlyStarred ? "" : "1")} data-testid="explore-quick-starred" className={chip(onlyStarred)}>My shortlist (<span data-no-translate>{starred.length}</span>)</button>
        {QUICK.map(([k, label]) => <button key={k} onClick={() => toggleQuick(k)} data-testid={`explore-quick-${k}`} className={chip(quick.includes(k))}>{label}</button>)}
      </div>
      <div className="mt-3 flex flex-wrap gap-2" data-testid="explore-cluster-filters">
        <button onClick={() => set("cluster", "")} data-testid="explore-cluster-all" className={chip(!cluster)}>All fields</button>
        {data?.clusters.map((c) => (
          <button key={c.id} onClick={() => set("cluster", cluster === c.id ? "" : c.id)} data-testid={`explore-cluster-${c.id}`} className={`${chip(cluster === c.id)} inline-flex items-center gap-1.5`}>
            <span className="w-2 h-2 rounded-full" style={{ background: c.color }} />{c.label}
          </button>
        ))}
      </div>

      {!data ? <Loader2 className="mt-12 animate-spin text-[#D96B43]" /> : (
        <>
          <p className="mt-8 text-sm text-slate-500" data-testid="explore-result-count"><span data-no-translate>{shown.length}</span> / <span data-no-translate>{indexed.length}</span> careers shown</p>
          {shown.length === 0 ? (
            <p className="mt-6 text-slate-600" data-testid="explore-empty">No careers match. Try a shorter word or clear a filter.</p>
          ) : (
            <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{shown.map((c, i) => <CareerCard key={c.id} c={c} color={colors[c.cluster]} i={i} />)}</div>
          )}
        </>
      )}

      <div className="mt-14 rounded-2xl bg-[#1C242B] text-[#FAF8F5] p-8 flex flex-col sm:flex-row sm:items-center gap-5">
        <p className="text-lg font-display font-semibold flex-1">Not sure where to start? The free test ranks all of these careers by how well they fit you.</p>
        <Link to="/assessment" data-testid="explore-take-test-btn" className="rounded-full bg-[#D96B43] px-6 py-3 text-sm font-semibold text-center hover:bg-[#C05630] transition-colors">Take the free test</Link>
      </div>
    </div>
  );
}
